import { useFormik, FormikProvider, Field, Form as FormikForm } from "formik"
import { Button, Col, Form } from "react-bootstrap"
import * as Yup from "yup"
import styles from "./AccountManagement.module.scss"
import { getCustomerInfo, updateCustomerInfo } from "shared/routes/account/account-management"
import getCounties from "shared/routes/account/counties"
import { useModel } from "@stem-sims/nexus"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { useContext, useEffect, useState } from "react"
import { UpdateAccountInformationProps } from "shared/types/accountManagementTypes"
import { AuthContext } from "AuthContext"

const InformationPage = () => {
    const { isSSO } = useContext(AuthContext)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [counties, setCounties] = useState<string[]>([])
    const [loadingCounties, setLoadingCounties] = useState(false)

    const { response: information, loading, refreshModel } = useModel({
        model: getCustomerInfo
    })

    const initialValues = {
        name: information?.name ?? "",
        email: information?.email ?? "",
        state: information?.state,
        county: information?.county,
        password: "",
    }

    function onSubmit (values: UpdateAccountInformationProps) {
        setIsSubmitting(true)
        if (values.state === null) values.state = undefined // set to undefined so that the api could receive it
        if (values.county === null) values.county = undefined

        updateCustomerInfo(values)
        .then(() => {
            toast.success("Information updated successfully.")
            refreshModel()
        })
        .catch((err) => {
            toast.error(err?.response?.data ?? "There has been an error updating the information.")
        })
        .finally(() => {
            setIsSubmitting(false)
            formik.resetForm()
            setCounties([])
        })
    }

    useEffect(() => {
        if (!information || !information.state || isSubmitting) return
        if (information?.state) {
            setLoadingCounties(true)
            getCounties(information.state)
                .then(setCounties)
                .catch(() => {
                    toast.error("There was an error populating counties data. Please try again.")
                    setCounties([])
                })
                .finally(() => {
                    setLoadingCounties(false)
                })
        } else {
            setCounties([])
        }
    }, [information, isSubmitting])

    async function populateCounties(event: React.ChangeEvent<HTMLSelectElement>) {
        formik.setFieldValue("county", null) // reset county when state changes
        setLoadingCounties(true)
        formik.handleChange(event)
        const newState = event.currentTarget.value
        setCounties(await getCounties(newState))
        setLoadingCounties(false)
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                name: !isSSO && Yup
                    .string()
                    .max(40)
                    .label("Name")
                    .required("Name is required."),
                email: !isSSO && Yup
                    .string()
                    .label("Email")
                    .required("Email is required."),
                state: Yup
                    .string()
                    .label("State")
                    .nullable()
                    .notRequired(),
                county: Yup
                    .string()
                    .label("County")
                    .nullable()
                    .notRequired(),
                password: !isSSO && Yup
                    .string()
                    .label("Password")
                    .required("Password is required."),
            })
    })

    if (loading || !information) return <BeatLoader />

  return (
    <div>
        <h4 className="mt-4">Update Account Information</h4>
        <div className="d-flex align-items-center justify-content-center mt-4">
            <FormikProvider value={formik}>
                <Form as={FormikForm} className={`text-center ${styles.container} border border-1 border-secondary rounded p-3`}>
                    {!isSSO && 
                    <>
                        <Form.Group controlId="formCreateName" className="my-4">
                            <Form.Label className={`${styles.label} d-block text-muted`}>Name</Form.Label>
                            <Form.Control
                                className={`${styles.input}`}
                                name="name"
                                type="text"
                                placeholder="Name"
                                as={Field}
                                onChange={formik.handleChange}
                                value={formik.values.name}
                                isInvalid={formik.touched.name && !!formik.errors.name}
                            />
                            <Form.Control.Feedback type="invalid" className="text-start">
                                {formik.errors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="formCreateEmail" className="my-4">
                            <Form.Label className={`${styles.label} d-block text-muted`}>Email Address</Form.Label>
                            <Form.Control
                                className={`${styles.input}`}
                                name="email"
                                type="text"
                                placeholder="Email Address"
                                as={Field}
                                onChange={formik.handleChange}
                                value={formik.values.email}
                                isInvalid={formik.touched.email && !!formik.errors.email}
                            />
                            <Form.Control.Feedback type="invalid" className="text-start">
                                {formik.errors.email}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </>
                    }

                    <Form.Group controlId="formCreateState" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>State</Form.Label>
                        <Form.Select
                            className={`${styles.input}`}
                            name="state"
                            placeholder="Select state"
                            onChange={populateCounties}
                            value={formik.values.state ?? ""}
                            isInvalid={formik.touched.state && !!formik.errors.state}>
                                <option value="" disabled>-- Select state</option>
                                {information.availableStates.map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.state}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formCreateCounty" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>County</Form.Label>
                        <Form.Select
                            className={`${styles.input}`}
                            name="county"
                            placeholder="Select county"
                            onChange={formik.handleChange}
                            value={formik.values.county ?? ""}
                            isInvalid={formik.touched.county && !!formik.errors.county}>
                                <option value="" disabled>{loadingCounties ? "Loading..." : (counties.length > 0 ? "-- Select county" : "-- Select state first")}</option>
                                {counties.map(county => (
                                    <option key={county} value={county} selected={county === formik.values.county}>{county}</option>
                                ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.county}
                        </Form.Control.Feedback>
                    </Form.Group>
                    {!isSSO && <Form.Group controlId="formCreatePassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Current Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="password"
                            type="password"
                            placeholder="Current Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.password}
                            isInvalid={formik.touched.password && !!formik.errors.password}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.password}
                        </Form.Control.Feedback>
                    </Form.Group>}
                    <Button
                        className="rounded-lg px-4 py-2 mt-4"
                        variant="theme"
                        disabled={!formik.dirty || !formik.isValid || (!isSSO && !formik.values.password) || isSubmitting}
                        type="submit">
                            Update Information
                    </Button>
                </Form>
            </FormikProvider>
        </div>
    </div>
  )
}

export default InformationPage
